import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import EmbedRoot from "./EmbedRoot";
import { captureClickIds } from "@/lib/tracking";
import "./styles/global.css";

const ELEMENT_NAME = "komilfo-funnel";

// Capture des click IDs (gclid/gbraid/wbraid) au chargement du bundle, avant
// même que le custom element ne soit instancié, pour ne pas dépendre du
// moment où l'utilisateur scrolle jusqu'au formulaire.
if (typeof window !== "undefined") {
  captureClickIds();
}

/**
 * Custom element `<komilfo-funnel></komilfo-funnel>`.
 *
 * Rendu Light DOM (pas de Shadow DOM) : le CSS est servi à côté et scopé via
 * le sélecteur `komilfo-funnel { ... }` dans global.css. Shadow DOM aurait
 * isolé proprement les styles mais compliqué le rendu des @font-face et du
 * widget Turnstile — compromis volontaire.
 *
 * Le custom element est enregistré une seule fois, même si le bundle est
 * chargé plusieurs fois (ex. preview + production sur la même page).
 */
class KomilfoFunnelElement extends HTMLElement {
  private root: Root | null = null;

  connectedCallback() {
    if (this.root) return;
    // Attribut optionnel `produits` : liste de produits séparée par des
    // virgules (ex: produits="pergola, store, volet"). Prime sur `variant`.
    // `produit` au singulier est accepté comme alias, c'est l'erreur de frappe
    // la plus probable côté Webflow.
    const produits =
      this.getAttribute("produits") ?? this.getAttribute("produit") ?? undefined;
    // Attribut optionnel `variant` (ex: <komilfo-funnel variant="solaire">).
    // Lu une fois au montage : Webflow le pose statiquement dans l'embed.
    const variant = this.getAttribute("variant") ?? undefined;
    // Attribut optionnel `localisation` : "magasin" active le flux région→magasin.
    // Absent/inconnu ⇒ "cp" (résolu par getLocalisationMode côté EmbedRoot).
    const localisation = this.getAttribute("localisation") ?? undefined;
    this.root = createRoot(this);
    this.root.render(
      <StrictMode>
        <EmbedRoot
          produits={produits}
          variant={variant}
          localisation={localisation}
        />
      </StrictMode>,
    );
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

if (typeof window !== "undefined" && !customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, KomilfoFunnelElement);
}
